/**
 * ストリートビュー3Dパノラマ重畳 (docs/25 §11、Increment 3、必須機能)。
 *
 * `post.streetView.panoId` で固定してパノラマを開く (lat/lng指定は撮影更新でズレるため
 * 使わない、docs §9-1と同じ理由)。`pins[]` はネイティブ `google.maps.Marker` では配置しない —
 * Marker は緯度経度 (=常に地上高さ0m) にしか配置できず「高さ」を表現できないため、木の
 * 枝葉のような対象が根元に表示されてしまう問題があった (2026-07-14、ユーザー指摘)。
 * 代わりに、現在のパノラマ視点 (heading/pitch/zoom) を基準に対象の方位・仰角から
 * スクリーン座標を毎フレーム再計算するカスタムARオーバーレイ (`projectToScreen`) で描画する。
 * これにより box2d の垂直位置 (仰角) がピンの見た目の高さに反映される。
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Card } from '@citizen-walk/shared-ui';
import type { PrimaryAxisResolution } from './streetSurveyAxisTypes';
import type { RoleTypeAxisResolution } from './streetSurveyAxisTypes';
import type { StreetSurveyPost } from './useStreetSurveyPosts';
import { useStreetViewBrowserKey } from './useStreetViewBrowserKey';
import { useGoogleMapsLoader } from './useGoogleMapsLoader';
import {
  computeMarkerAzimuth,
  computeMarkerElevation,
  computeVerticalFovDeg,
  computeVerticalCenterRatio,
  projectToScreen,
  zoomToHFovDeg,
} from './streetViewGeometry';
import {
  resolvePinColor,
  resolvePinRoleColor,
  resolvePinRoleEmoji,
  resolvePinTooltip,
} from './streetSurveyPinDisplay';

// 2D 画像ボードの「ぼんやり領域」表示の3D近似 (2026-07-14)。フォーカス中のピンにのみ、
// 距離に応じて拡縮する柔らかいグラデーション円を重ねて「強調された領域」を近似する。
// 2D の AREA_SCALE_* と同じ距離補正ロジックを踏襲。
const GLOW_BASE_SIZE_PX = 90;
const GLOW_SCALE_MIN = 0.6;
const GLOW_SCALE_MAX = 1.4;
const GLOW_SCALE_NEAR_M = 5;
const GLOW_SCALE_FAR_M = 40;

function resolveGlowScale(distanceMeters: number): number {
  const t = Math.min(
    1,
    Math.max(0, (distanceMeters - GLOW_SCALE_NEAR_M) / (GLOW_SCALE_FAR_M - GLOW_SCALE_NEAR_M)),
  );
  return GLOW_SCALE_MAX - t * (GLOW_SCALE_MAX - GLOW_SCALE_MIN);
}

interface PovState {
  heading: number;
  pitch: number;
  zoom: number;
}

export interface StreetSurveyPanoramaProps {
  districtId: string;
  post: StreetSurveyPost;
  primaryAxis: PrimaryAxisResolution;
  roleTypeAxis?: RoleTypeAxisResolution;
  highlightedRank?: number | null;
  onSelectRank?: (rank: number) => void;
  /** M3 outputFormat.schema.reasoningField (mode ごとにキー名が異なるため決め打ちしない、2026-07-14)。 */
  reasoningField?: string;
}

export function StreetSurveyPanorama({
  districtId,
  post,
  primaryAxis,
  roleTypeAxis,
  highlightedRank,
  reasoningField,
  onSelectRank,
}: StreetSurveyPanoramaProps) {
  const keyState = useStreetViewBrowserKey(districtId);
  const apiKey = keyState.status === 'ready' ? keyState.apiKey : null;
  const loaderState = useGoogleMapsLoader(apiKey);

  const containerRef = useRef<HTMLDivElement>(null);
  const panoramaRef = useRef<google.maps.StreetViewPanorama | null>(null);
  const onSelectRef = useRef(onSelectRank);
  useEffect(() => {
    onSelectRef.current = onSelectRank;
  }, [onSelectRank]);

  const [pov, setPov] = useState<PovState | null>(null);
  const [containerSize, setContainerSize] = useState<{ width: number; height: number } | null>(
    null,
  );

  const streetView = post.streetView;

  // パノラマ初期化 (loaderState が ready になった時点で1回) + 視点(pov)/ズームの監視。
  useEffect(() => {
    if (loaderState.status !== 'ready' || !containerRef.current || !streetView) return;
    // `pano: <panoId>` で直接初期化すると (実機検証で確認)、位置・map ともに正しく設定された
    // Marker/オーバーレイが一切画面に描画されない不具合が Maps JS API 側にある。`position`
    // 指定で初期化すると正常描画されるため、まず `position` (=このパノラマ自身の撮影地点) で
    // 初期化し、解決された panoId が保存済みの panoId とズレていた場合のみ `setPano` で
    // 強制的にロックし直す (撮影更新によるズレ回避、docs/25 §9-1 の要件は維持)。
    const panorama = new google.maps.StreetViewPanorama(containerRef.current, {
      position: { lat: streetView.panoLocation.latitude, lng: streetView.panoLocation.longitude },
      pov: { heading: streetView.baseHeading, pitch: 0 },
      // zoom を明示しないと Maps JS の既定 (zoom=1、画角45°相当) になり、`zoomToHFovDeg` が
      // 前提とする撮影時の画角 (fov=90、zoom=0相当) とズレて box2d 由来のピンが軒並み画角外に
      // 落ちてしまう (2026-07-14 実機検証で判明)。撮影画像と揃えるため zoom=0 を明示する。
      zoom: 0,
      addressControl: false,
      showRoadLabels: false,
      linksControl: false,
    });
    panoramaRef.current = panorama;
    const targetPanoId = streetView.panoId;
    const statusListener = google.maps.event.addListenerOnce(panorama, 'status_changed', () => {
      if (
        panorama.getStatus() === google.maps.StreetViewStatus.OK &&
        panorama.getPano() !== targetPanoId
      ) {
        panorama.setPano(targetPanoId);
      }
    });

    const syncPov = () => {
      const p = panorama.getPov();
      setPov({ heading: p.heading, pitch: p.pitch, zoom: panorama.getZoom() });
    };
    syncPov();
    const povListener = panorama.addListener('pov_changed', syncPov);
    const zoomListener = panorama.addListener('zoom_changed', syncPov);

    return () => {
      google.maps.event.removeListener(statusListener);
      google.maps.event.removeListener(povListener);
      google.maps.event.removeListener(zoomListener);
      panoramaRef.current = null;
      setPov(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loaderState.status, streetView?.panoId]);

  // コンテナの実サイズ監視 (投影計算に必要、レスポンシブ幅に追従)。
  useEffect(() => {
    const el = containerRef.current;
    if (!el || loaderState.status !== 'ready') return;
    const update = () => setContainerSize({ width: el.clientWidth, height: el.clientHeight });
    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, [loaderState.status]);

  const pinOverlays = useMemo(() => {
    if (!streetView || !pov || !containerSize || containerSize.width === 0) return [];
    // 静止画取得時の画角。Static API の fov は水平画角なので、仰角の換算には size の
    // 縦横比から導いた垂直画角を使う (2026-07-16、それ以前は水平 fov を垂直にも適用していた)。
    const captureHFov = streetView.imageParams.fov;
    const captureVFov = computeVerticalFovDeg(captureHFov, streetView.imageParams.size);
    // こちらは静止画ではなくパノラマビューアの現在の水平画角 (zoom 由来)。
    const hFovDeg = zoomToHFovDeg(pov.zoom);
    // streetView.pins は M3 の分類配列 maxItems で切り詰められる前の座標を持つため、
    // 対応する分類データが無いランクは描画しない (StreetSurveyImageBoard.tsx と同じ理由、2026-07-14)。
    const classifiedRanks = new Set(post.classifications.map((c) => c.rank));
    return streetView.pins
      .filter((pin) => classifiedRanks.has(pin.rank))
      .map((pin) => {
        const azimuth = computeMarkerAzimuth(
          streetView.baseHeading,
          pin.imageIndex,
          pin.box2d,
          captureHFov,
        );
        const elevation = computeMarkerElevation(pin.box2d, captureVFov);
        const projection = projectToScreen({
          targetAzimuthDeg: azimuth,
          targetElevationDeg: elevation,
          cameraHeadingDeg: pov.heading,
          cameraPitchDeg: pov.pitch,
          hFovDeg,
          containerWidth: containerSize.width,
          containerHeight: containerSize.height,
        });
        const isHighlighted = pin.rank === highlightedRank;
        const verticalRatio = computeVerticalCenterRatio(pin.box2d);
        const emoji = roleTypeAxis ? resolvePinRoleEmoji(post, roleTypeAxis, pin.rank) : undefined;
        // ピンの色は 2D 画像ボードと同じく凡例 (色分けする調査項目) に合わせ、無いときだけ
        // 集計の調査項目の色にフォールバックする。
        const fillColor =
          (roleTypeAxis ? resolvePinRoleColor(post, roleTypeAxis, pin.rank) : undefined) ??
          resolvePinColor(post, primaryAxis, pin.rank);
        const glowSizePx = GLOW_BASE_SIZE_PX * resolveGlowScale(pin.distanceMeters);
        return {
          rank: pin.rank,
          x: projection.x,
          y: projection.y,
          visible: projection.visible,
          isHighlighted,
          emoji,
          fillColor,
          glowSizePx,
          title: `${resolvePinTooltip(post, primaryAxis, pin.rank, roleTypeAxis, reasoningField)} / 検出位置: 画像内上から${Math.round(verticalRatio * 100)}% / 距離${pin.distanceMeters}m`,
        };
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [streetView, pov, containerSize, highlightedRank, primaryAxis, roleTypeAxis, reasoningField]);

  if (!streetView) return null;

  if (keyState.status === 'disabled') {
    return (
      <Card padding="md">
        <p className="text-sm text-slate-500">
          この地区ではストリートビュー3D表示が有効になっていません（地区の「AI設定」ページで有効化してください）。
        </p>
      </Card>
    );
  }
  if (keyState.status === 'error' || loaderState.status === 'error') {
    const error =
      keyState.status === 'error' ? keyState.error : (loaderState as { error: string }).error;
    return (
      <Card padding="md" className="border-danger-600">
        <p className="text-sm text-danger-700">3Dパノラマの読込に失敗しました: {error}</p>
      </Card>
    );
  }
  if (loaderState.status !== 'ready') {
    return (
      <Card padding="md">
        <p className="text-sm text-slate-500">3Dパノラマを読込中…</p>
      </Card>
    );
  }

  return (
    <div className="relative w-full h-[500px] rounded-lg border border-slate-200 overflow-hidden">
      <div ref={containerRef} className="w-full h-full" data-testid="street-survey-panorama" />
      {/* Google 側が StreetViewPanorama のコンテナに `position:relative` を内部付与し、その
          WebGL canvas が独自のコンポジットレイヤーとして扱われるため、明示的な z-index が
          無いと DOM順序を無視して canvas がこのオーバーレイより手前に描画されてしまう
          (2026-07-14 実機検証で判明)。z-index を明示してレイヤー順序を強制する。 */}
      <div
        className="absolute inset-0 overflow-hidden"
        style={{ pointerEvents: 'none', zIndex: 10 }}
      >
        {pinOverlays.map((p) =>
          p.visible ? (
            <div
              key={p.rank}
              className="absolute -translate-x-1/2 -translate-y-1/2"
              style={{ left: p.x, top: p.y }}
            >
              {p.isHighlighted && (
                <div
                  className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full"
                  style={{
                    width: p.glowSizePx,
                    height: p.glowSizePx,
                    background: `radial-gradient(ellipse at center, ${p.fillColor} 0%, ${p.fillColor} 45%, transparent 85%)`,
                    opacity: 0.8,
                  }}
                />
              )}
              <button
                type="button"
                title={p.title}
                onClick={(e) => {
                  // ページ側の「他の場所をクリックしたら選択解除」ハンドラへの伝播を止める
                  // (2026-07-21、StreetSurveyDetailPage.tsx の外側クリック解除機能追加時に対応)。
                  e.stopPropagation();
                  onSelectRef.current?.(p.rank);
                }}
                className="relative flex items-center justify-center rounded-full text-white text-[11px] font-bold cursor-pointer"
                style={{
                  pointerEvents: 'auto',
                  width: p.isHighlighted ? 26 : 20,
                  height: p.isHighlighted ? 26 : 20,
                  backgroundColor: p.fillColor,
                  border: '2px solid white',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.5)',
                }}
              >
                {p.emoji ?? p.rank}
              </button>
            </div>
          ) : null,
        )}
      </div>
    </div>
  );
}
